import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DataTable, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Select } from '@/components/ui/primitives';
import { AttendanceTab, LeaveTab, PayrollTab, RosterTab } from '@/features/hr/HrTabs';
import { CloneRoleDialog } from './CloneRoleDialog';
import { PermissionOverridesDialog } from './PermissionOverridesDialog';
import { ROLES_RETURN_TO } from './RolePage';
import { RolesTab } from './RolesTab';
import { ExportUsersButton, UserControlsMenu } from './UserControls';
import { holdsSuperAdmin, PEOPLE_QUERY_KEY, type PersonPage, type PersonRow, type Role } from './types';

const TAB_IDS = ['people', 'roles', 'roster', 'attendance', 'leave', 'payroll'] as const;
type TabId = (typeof TAB_IDS)[number];
const isTabId = (value: string | null): value is TabId => TAB_IDS.includes(value as TabId);
type PeopleStatus = 'active' | 'inactive' | 'all';

interface LicenceAlert {
  id: string;
  name: string;
  licenseNo: string | null;
  licenseExpiry: string;
  daysRemaining: number;
  expired: boolean;
}

function LicenceAlerts() {
  const licences = useQuery({
    queryKey: ['licences'],
    queryFn: () => get<LicenceAlert[]>('/hr/alerts/licences?withinDays=90'),
  });
  const rows = licences.data ?? [];
  if (rows.length === 0) return null;
  const expired = rows.filter((row) => row.expired);
  return (
    <Alert variant={expired.length ? 'destructive' : 'warning'} className="mb-4">
      <span className="inline-flex items-center gap-1 font-semibold">
        <AlertTriangle className="h-4 w-4" />
        {expired.length
          ? tr('admin', 'x_professional_registration_s_have_expired', { count: expired.length })
          : tr('admin', 'x_registration_s_expire_within_90_days', { count: rows.length })}
      </span>
      <p className="mt-1 text-xs">
        {rows
          .slice(0, 6)
          .map((row) => `${row.name} (${row.expired ? tr('admin', 'expired') : `${row.daysRemaining} d`})`)
          .join(' · ')}
      </p>
    </Alert>
  );
}

export function PeoplePage() {
  const { can, canWrite, hasModule, isSuperAdmin, user: session } = useAuth();
  const hr = hasModule('hr');
  const manageUsers = can('user.manage') && canWrite;
  const manageEmployees = hr && can('hr.employee.manage') && canWrite;
  const managePeople = manageUsers || manageEmployees;
  const manageRoles = isSuperAdmin && canWrite;
  const showPeople = can('user.read', 'user.manage', 'hr.employee.read', 'hr.employee.manage');
  const showRoles = can('user.read', 'role.read');
  const showRosters = hr && can('hr.roster.manage', 'hr.employee.read');
  const showAttendance = hr && can('hr.attendance.manage', 'hr.employee.read');
  const showLeave = hr && can('hr.leave.approve', 'hr.employee.read', 'hr.self.read');
  const showPayroll = hr && can('hr.payroll.run');

  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const visibleTabs: Record<TabId, boolean> = {
    people: showPeople,
    roles: showRoles,
    roster: showRosters,
    attendance: showAttendance,
    leave: showLeave,
    payroll: showPayroll,
  };
  const fallback: TabId = TAB_IDS.find((id) => visibleTabs[id]) ?? 'people';
  const tab: TabId = isTabId(requested) && visibleTabs[requested] ? requested : fallback;
  const selectTab = (id: string) => setParams(id === 'people' ? {} : { tab: id }, { replace: true });

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PeopleStatus>('active');
  const [managingPermissions, setManagingPermissions] = useState<PersonRow | null>(null);
  const [cloning, setCloning] = useState<Role | null>(null);
  const openPerson = (row: PersonRow | null) => navigate(row ? `/people/${row.key}/edit` : '/people/new');
  const openRole = (role: Role | null) =>
    navigate(`${role ? `/people/roles/${role.id}/edit` : '/people/roles/new'}?returnTo=${encodeURIComponent(ROLES_RETURN_TO)}`);
  const people = useInfiniteQuery({
    queryKey: [...PEOPLE_QUERY_KEY, search, status],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<PersonPage>(
        `/people${qs({ q: search || undefined, status: status === 'all' ? undefined : status, limit: 50, cursor: pageParam || undefined })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: tab === 'people' && showPeople,
  });
  const rows = people.data?.pages.flatMap((page) => page.data);

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => get<Role[]>('/org/roles'),
    enabled: showRoles || manageUsers,
  });

  return (
    <>
      <PageHeader
        title={tr('admin', 'people')}
        description={tr('admin', 'everyone_who_works_here_with_or_without_a_login')}
        actions={
          tab === 'people'
            ? (
                <div className="flex flex-wrap gap-2">
                  {can('user.read', 'user.manage') && <ExportUsersButton />}
                  {managePeople && <Button onClick={() => openPerson(null)}>{tr('admin', 'add_person')}</Button>}
                </div>
              )
            : tab === 'roles'
              ? manageRoles && <Button onClick={() => openRole(null)}>{tr('admin', 'add_role')}</Button>
              : null
        }
      />

      <Tabs
        active={tab}
        onChange={selectTab}
        tabs={[
          ...(showPeople ? [{ id: 'people', label: tr('admin', 'people') }] : []),
          ...(showRoles ? [{ id: 'roles', label: tr('admin', 'roles'), badge: <Badge variant="muted">{roles.data?.length ?? 0}</Badge> }] : []),
          ...(showRosters ? [{ id: 'roster', label: tr('admin', 'rosters') }] : []),
          ...(showAttendance ? [{ id: 'attendance', label: tr('admin', 'attendance') }] : []),
          ...(showLeave ? [{ id: 'leave', label: tr('admin', 'leave') }] : []),
          ...(showPayroll ? [{ id: 'payroll', label: tr('admin', 'payroll') }] : []),
        ]}
      />

      {tab === 'people' && showPeople && (
        <>
          {hr && can('hr.employee.read', 'hr.employee.manage') && <LicenceAlerts />}
          {can('user.manage', 'hr.employee.manage') && !canWrite && (
            <Alert className="mb-3">
              {tr('admin', 'changes_are_made_on_your_facilitys_own_server')}
            </Alert>
          )}
          <SearchFilterBar
            search={{ value: search, onChange: setSearch, placeholder: tr('admin', 'search_by_name_username_or_employee_number') }}
            filters={
              <FilterField label={tr('admin', 'status')}>
                <Select className="w-40" value={status} onChange={(event) => setStatus(event.target.value as PeopleStatus)}>
                  <option value="active">{tr('admin', 'status_active')}</option>
                  <option value="inactive">{tr('admin', 'status_inactive')}</option>
                  <option value="all">{tr('admin', 'status_everyone')}</option>
                </Select>
              </FilterField>
            }
            onReset={
              search || status !== 'active'
                ? () => {
                    setSearch('');
                    setStatus('active');
                  }
                : undefined
            }
          />
          <DataTable
            rows={rows}
            loading={people.isLoading} refreshing={people.isFetching}
            error={people.error}
            rowKey={(row) => row.key}
            onRowClick={managePeople ? (row) => openPerson(row) : undefined}
            empty={tr('admin', 'no_people_yet')}
            columns={[
              {
                header: tr('admin', 'name'),
                sort: (row) => row.name,
                cell: (row) => (
                  <div>
                    <p className="font-medium">{row.name}</p>
                    <p className="text-xs text-muted-foreground">{row.designation ?? '—'}</p>
                  </div>
                ),
              },
              {
                header: tr('admin', 'record'),
                cell: (row) => (
                  <div className="flex flex-wrap gap-1">
                    {row.userId && (
                      <Badge variant="secondary" title={row.username ?? undefined}>
                        {tr('admin', 'login')}
                      </Badge>
                    )}
                    {row.employeeId && (
                      <Badge variant="outline" title={row.employeeNo ?? undefined}>
                        {tr('admin', 'hr_record')}
                      </Badge>
                    )}
                  </div>
                ),
              },
              {
                header: tr('admin', 'departments'),
                sort: (row) => row.department,
                cell: (row) => (
                  <span className="block max-w-xs truncate" title={row.department ?? undefined}>
                    {row.department ?? '—'}
                  </span>
                ),
              },
              { header: tr('admin', 'branch'), sort: (row) => row.branch, cell: (row) => row.branch ?? '—' },
              {
                header: tr('admin', 'roles'),
                cell: (row) => (
                  <div className="flex flex-wrap gap-1">
                    {row.roles.map((role) => (
                      <Badge key={role.id} variant="secondary">
                        {role.name}
                      </Badge>
                    ))}
                    {row.roles.length === 0 && <span className="text-xs text-muted-foreground">{row.userId ? tr('admin', 'none') : '—'}</span>}
                  </div>
                ),
              },
              {
                header: tr('admin', 'clinical'),
                cell: (row) => (
                  <div className="flex flex-wrap gap-1">
                    {row.isDoctor && <Badge variant="default">{tr('admin', 'doctor')}</Badge>}
                    {row.acceptsAppointments && <Badge variant="success">{tr('admin', 'bookable')}</Badge>}
                    {!row.isDoctor && !row.acceptsAppointments && '—'}
                  </div>
                ),
              },
              {
                header: tr('admin', 'status'),
                sort: (row) => row.isActive,
                cell: (row) => (
                  <div className="flex flex-wrap gap-1">
                    <Badge variant={row.isActive ? 'success' : 'destructive'}>{row.isActive ? tr('admin', 'active') : tr('admin', 'inactive')}</Badge>
                    {row.lockedUntil && <Badge variant="warning">{tr('admin', 'locked_out')}</Badge>}
                  </div>
                ),
              },
              {
                header: '',
                align: 'right',
                cell: (row) =>
                  managePeople && (isSuperAdmin || !holdsSuperAdmin(row.roles)) ? (
                    <div className="flex justify-end gap-2">
                      {manageUsers && row.userId && row.userId !== session?.id && !holdsSuperAdmin(row.roles) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setManagingPermissions(row);
                          }}
                        >
                          {tr('admin', 'permissions')}
                        </Button>
                      )}
                      <Button variant="secondary" size="sm" onClick={() => openPerson(row)}>
                        {tr('admin', 'edit')}
                      </Button>
                      {row.userId && (
                        <UserControlsMenu user={{ id: row.userId, name: row.name, isActive: row.isActive, roles: row.roles }} />
                      )}
                    </div>
                  ) : null,
              },
            ]}
            footer={
              people.hasNextPage && (
                <Button variant="secondary" size="sm" loading={people.isFetchingNextPage} onClick={() => void people.fetchNextPage()}>
                  {tr('admin', 'load_more')}
                </Button>
              )
            }
          />
        </>
      )}

      {tab === 'roles' && showRoles && <RolesTab manageRoles={manageRoles} onEdit={openRole} onClone={setCloning} />}
      {tab === 'roster' && showRosters && <RosterTab />}
      {tab === 'attendance' && showAttendance && <AttendanceTab />}
      {tab === 'leave' && showLeave && <LeaveTab />}
      {tab === 'payroll' && showPayroll && <PayrollTab />}

      {cloning !== null && <CloneRoleDialog role={cloning} onClose={() => setCloning(null)} />}

      {managingPermissions !== null && managingPermissions.userId && (
        <PermissionOverridesDialog
          user={{ id: managingPermissions.userId, name: managingPermissions.name }}
          onClose={() => setManagingPermissions(null)}
        />
      )}
    </>
  );
}
